import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, Translations, getTranslation } from '../i18n/translations';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Get initial language from localStorage or browser, default to Swedish
  const getInitialLanguage = (): Language => {
    const stored = localStorage.getItem('appLanguage') as Language;
    if (stored && ['sv', 'en', 'da', 'no', 'fi'].includes(stored)) {
      return stored;
    }
    
    // Try to detect from browser
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith('en')) return 'en';
    if (browserLang.startsWith('da')) return 'da';
    if (browserLang.startsWith('no') || browserLang.startsWith('nb') || browserLang.startsWith('nn')) return 'no';
    if (browserLang.startsWith('fi')) return 'fi';
    
    return 'sv'; // Default to Swedish
  };

  const [language, setLanguageState] = useState<Language>(getInitialLanguage);
  const [t, setT] = useState<Translations>(getTranslation(language));

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    setT(getTranslation(lang));
    localStorage.setItem('appLanguage', lang);
    
    // Update HTML lang attribute
    document.documentElement.lang = lang;
    
    // Update meta tags and title
    updateMetaTags(lang, getTranslation(lang));
  };

  const updateMetaTags = (lang: Language, translations: Translations) => {
    // Update page title
    document.title = translations.appTitle;
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', translations.welcomeSubtitle);
    
    // Update Open Graph tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', translations.appTitle);
    
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.setAttribute('content', translations.welcomeSubtitle);
    
    let ogLocale = document.querySelector('meta[property="og:locale"]');
    if (!ogLocale) {
      ogLocale = document.createElement('meta');
      ogLocale.setAttribute('property', 'og:locale');
      document.head.appendChild(ogLocale);
    }
    const localeMap: Record<Language, string> = {
      sv: 'sv_SE',
      en: 'en_US',
      da: 'da_DK',
      no: 'nb_NO',
      fi: 'fi_FI'
    };
    ogLocale.setAttribute('content', localeMap[lang]);
  };

  useEffect(() => {
    setT(getTranslation(language));
    // Update meta tags on initial load and language change
    document.documentElement.lang = language;
    updateMetaTags(language, getTranslation(language));
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
